/** Compare two revisions three ways, and never let one of them look like the answer.
 *
 * The overlay says WHERE, the text diff says WHAT, the model diff says which objects.
 * Each one prints its own limitation, because a comparison that looks authoritative is
 * how people stop checking the drawing.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { useNfaTheme } from '@/components/nfa/nfaTheme';
import { Btn, Empty, Field, Panel, Select } from '@/components/nfa/NfaPrimitives';

const MODES = [['overlay', '2D overlay'], ['text', 'Text diff'], ['model', '3D model diff']];

const EdmCompare = ({ projectId }) => {
  const T = useNfaTheme();
  const [docs, setDocs] = useState([]);
  const [docId, setDocId] = useState('');
  const [revs, setRevs] = useState([]);
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [mode, setMode] = useState('overlay');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/edm/projects/${projectId}/documents?limit=300`)
      .then((r) => setDocs(r.data.documents || [])).catch(() => {});
  }, [projectId]);

  useEffect(() => {
    if (!docId) { setRevs([]); return; }
    api.get(`/edm/documents/${docId}`).then((r) => {
      const rows = (r.data.revisions || []).slice().sort((x, y) => x.sequence - y.sequence);
      setRevs(rows);
      if (rows.length >= 2) {
        setA(rows[rows.length - 2].revision_id);
        setB(rows[rows.length - 1].revision_id);
      } else { setA(''); setB(''); }
    }).catch(() => setRevs([]));
  }, [docId]);

  const run = async () => {
    if (!a || !b) return;
    setBusy(true); setRes(null);
    try {
      const r = await api.get(`/edm/compare/${mode}?revision_a=${a}&revision_b=${b}`);
      setRes(r.data);
    } catch (e) {
      setRes({ available: false, reason: e?.response?.data?.detail || 'The comparison failed' });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="edm-compare">
      <Panel T={T} testId="edm-compare-picker" title="Compare two revisions"
        subtitle="The overlay says where, the text diff says what, the model diff says which objects. Use more than one."
        right={<Btn T={T} onClick={run} disabled={!a || !b || busy} testId="edm-compare-run">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Compare
        </Btn>}>
        <div className="grid md:grid-cols-4 gap-3">
          <Field T={T} label="Document">
            <Select T={T} value={docId} data-testid="edm-compare-doc"
              onChange={(e) => setDocId(e.target.value)}>
              <option value="">Pick a document…</option>
              {docs.map((d) => (
                <option key={d.document_id} value={d.document_id}>
                  {d.document_no} — {d.title}
                </option>
              ))}
            </Select>
          </Field>
          <Field T={T} label="Revision A (older)">
            <Select T={T} value={a} data-testid="edm-compare-a"
              onChange={(e) => setA(e.target.value)}>
              <option value="">—</option>
              {revs.map((r) => <option key={r.revision_id} value={r.revision_id}>
                Rev {r.revision_code}</option>)}
            </Select>
          </Field>
          <Field T={T} label="Revision B (newer)">
            <Select T={T} value={b} data-testid="edm-compare-b"
              onChange={(e) => setB(e.target.value)}>
              <option value="">—</option>
              {revs.map((r) => <option key={r.revision_id} value={r.revision_id}>
                Rev {r.revision_code}</option>)}
            </Select>
          </Field>
          <Field T={T} label="Comparison">
            <Select T={T} value={mode} data-testid="edm-compare-mode"
              onChange={(e) => setMode(e.target.value)}>
              {MODES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </Select>
          </Field>
        </div>
        {docId && revs.length < 2 && (
          <p className="mt-2 text-[10.5px]" style={{ color: T.amber }}
            data-testid="edm-compare-one-revision">
            This document has one revision, so there is nothing to compare it with yet.
          </p>
        )}
      </Panel>

      {res && res.available === false && (
        <Panel T={T} testId="edm-compare-unavailable" title="Not available">
          <p className="text-[11.5px]" style={{ color: T.body }}>{res.reason}</p>
        </Panel>
      )}

      {res?.available && mode === 'overlay' && (
        <Panel T={T} testId="edm-compare-overlay" title={`Overlay · page ${res.page} of ${res.pages}`}
          subtitle={res.note}>
          <div className="flex items-center gap-3 text-[10.5px] mb-2" style={{ color: T.muted }}>
            <span style={{ color: '#C21F1F' }}>■ only in Rev A</span>
            <span style={{ color: '#1F49C2' }}>■ only in Rev B</span>
            <span>■ in both</span>
            <span data-testid="edm-overlay-changed">{res.changedPct}% of the sheet differs</span>
          </div>
          <img src={res.png} alt="overlay" data-testid="edm-overlay-image"
            style={{ width: '100%', border: `1px solid ${T.border}`, background: '#fff' }} />
        </Panel>
      )}

      {res?.available && mode === 'text' && (
        <Panel T={T} testId="edm-compare-text" title="Text diff" subtitle={res.note}>
          <div className="grid md:grid-cols-2 gap-3 text-[11px]">
            <div>
              <div className="text-[9.5px] uppercase tracking-[0.08em] mb-1"
                style={{ color: T.red }}>removed ({res.removedCount})</div>
              <div className="rounded p-2 max-h-72 overflow-auto font-mono text-[10.5px]"
                data-testid="edm-diff-removed"
                style={{ background: T.tint(T.red, '14'), border: `1px solid ${T.border}` }}>
                {res.removed.length ? res.removed.map((l, i) => <div key={i}>- {l}</div>)
                  : <span style={{ color: T.muted }}>nothing removed</span>}
              </div>
            </div>
            <div>
              <div className="text-[9.5px] uppercase tracking-[0.08em] mb-1"
                style={{ color: T.green }}>added ({res.addedCount})</div>
              <div className="rounded p-2 max-h-72 overflow-auto font-mono text-[10.5px]"
                data-testid="edm-diff-added"
                style={{ background: T.tint(T.green, '14'), border: `1px solid ${T.border}` }}>
                {res.added.length ? res.added.map((l, i) => <div key={i}>+ {l}</div>)
                  : <span style={{ color: T.muted }}>nothing added</span>}
              </div>
            </div>
          </div>
          {res.approximate && (
            <p className="mt-2 text-[10.5px]" style={{ color: T.amber }}
              data-testid="edm-diff-approximate">{res.note}</p>
          )}
        </Panel>
      )}

      {res?.available && mode === 'model' && (
        <Panel T={T} testId="edm-compare-model" title="3D model diff" subtitle={res.note}>
          <div className="grid md:grid-cols-3 gap-3 text-[11px]">
            {[['added', res.added, T.green], ['removed', res.removed, T.red],
              ['changed', (res.changed || []).map((c) => `${c.object} · ${c.fields.join(', ')}`),
                T.amber]].map(([label, rows, tone]) => (
              <div key={label}>
                <div className="text-[9.5px] uppercase tracking-[0.08em] mb-1"
                  style={{ color: tone }}>{label} ({rows.length})</div>
                <div className="rounded p-2 max-h-64 overflow-auto font-mono text-[10px]"
                  data-testid={`edm-model-${label}`}
                  style={{ border: `1px solid ${T.border}` }}>
                  {rows.length ? rows.map((r, i) => <div key={i}>{r}</div>)
                    : <span style={{ color: T.muted }}>none</span>}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {!res && !busy && <Empty T={T} testId="edm-compare-idle">
        Pick a document and two revisions.</Empty>}
    </div>
  );
};

export default EdmCompare;
